import "./FormTemplatesListPage.scss";

import { Badge, Col, Row } from "react-bootstrap";
import React, { useEffect, useMemo, useState } from "react";
import { faAdd, faCalendar, faEye, faPen, faPowerOff, faTrash } from "@fortawesome/free-solid-svg-icons";

import ActionPillButton from "../../../../../commons/buttons/ActionPillButton";
import AppInput from "../../../../../commons/inputs/AppInput";
import CompanyAPI from "../../../../company/CompanyAPI";
import ConfirmModal from "../../../../../commons/modal/ConfirmModal";
import CustomCard from "../../../../../commons/cards/Card";
import EmptyState from "../../../../../commons/EmptyState";
import FloatingButton from "../../../../../commons/buttons/FloatingButton";
import FormTemplateAPI from "../../api/FormTemplateAPI";
import HallAppToast from "../../../../../commons/HallAppToast";
import MenuTitle from "../../../../../commons/menu/MenuTitle";
import Skeleton from "../../../../../commons/skeleton/Skeleton";
import SpaceMenuBottom from "../../../../../commons/SpaceMenuBottom";
import { useNavigate } from "react-router-dom";

const formAPI = new FormTemplateAPI();
const companyAPI = new CompanyAPI();

const typeLabel = (t) => {
  switch (t) {
    case "ANAMNESE":
      return "Anamnese";
    case "AVALIACAO":
      return "Avaliação";
    case "POS_ATENDIMENTO":
      return "Pós-atendimento";
    default:
      return "Outro";
  }
};

function formatDate(isoLike) {
  if (!isoLike) return "—";
  try {
    const d = new Date(isoLike);
    if (Number.isNaN(d.getTime())) return String(isoLike);
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(d);
  } catch {
    return String(isoLike);
  }
}

function FormTemplateCardSkeleton({ count = 6 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
      {Array.from({ length: count }).map((_, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={idx} className="ft-card ft-item">
          <div className="ft-item__top">
            <div className="ft-item__info" style={{ width: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <Skeleton variant="text" width="45%" height={16} />
                <Skeleton variant="rect" width={70} height={18} style={{ borderRadius: 999 }} />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <Skeleton variant="text" width="88%" height={12} />
                <Skeleton variant="text" width="72%" height={12} />
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <Skeleton variant="rect" width={74} height={18} style={{ borderRadius: 999 }} />
                <Skeleton variant="rect" width={64} height={18} style={{ borderRadius: 999 }} />
                <Skeleton variant="text" width="90px" height={12} />
                <Skeleton variant="text" width="140px" height={12} />
              </div>
            </div>
          </div>

          <div className="ft-item__actions" style={{ gap: 10 }}>
            <Skeleton variant="rect" width={110} height={30} style={{ borderRadius: 999 }} />
            <Skeleton variant="rect" width={110} height={30} style={{ borderRadius: 999 }} />
            <Skeleton variant="rect" width={110} height={30} style={{ borderRadius: 999 }} />
            <Skeleton variant="rect" width={110} height={30} style={{ borderRadius: 999 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function FormTemplatesListPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter] = useState("ALL"); // ALL | ACTIVE | INACTIVE (mantive a variável pra compat, mesmo não usando UI aqui)

  const [loadingList, setLoadingList] = useState(true);

  // busy por item (toggle/delete)
  const [busyById, setBusyById] = useState({});

  // modal delete
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const navigate = useNavigate();
  const companyId = useMemo(() => companyAPI.getCompanyManagment().id, []);

  useEffect(() => {
    getForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setBusy(id, v) {
    setBusyById((prev) => ({ ...prev, [id]: v }));
  }

  async function getForms() {
    setLoadingList(true);
    try {
      const resp = await formAPI.list(companyId);
      setItems(resp?.data || []);
    } catch (_) {
      setItems([]);
      HallAppToast.genericError();
    } finally {
      setLoadingList(false);
    }
  }

  const filtered = useMemo(() => {
    const term = String(q || "").trim().toLowerCase();

    return (items || [])
      .filter((it) => {
        if (filter === "ACTIVE") return it.active === true;
        if (filter === "INACTIVE") return it.active === false;
        return true;
      })
      .filter((it) => {
        if (!term) return true;
        return (
          String(it.name || "").toLowerCase().includes(term) ||
          String(it.description || "").toLowerCase().includes(term) ||
          String(typeLabel(it.templateType)).toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        return String(b.createDt || "").localeCompare(String(a.createDt || ""));
      });
  }, [items, q, filter]);

  const onCreate = () => navigate("/config-form");
  const onEdit = (id) => navigate(`/config-form/${id}`);
  const onView = (id) => {
    const url = `https://agendamento.hallapp.com.br/form/${id}`;
    window.open(url, "_blank");
  };

  const onToggleActive = async (form) => {
    if (!form?.id) return;
    if (busyById[form.id]) return;

    setBusy(form.id, true);
    try {
      await formAPI.updateStatus(form.id, !form.active);
      HallAppToast.success(!form.active ? "Formulário ativado!" : "Formulário inativado!");
      await getForms();
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setBusy(form.id, false);
    }
  };

  const onAskDelete = (form) => {
    setCurrentItem(form);
    setShowModalDelete(true);
  };

  async function executeDeletion() {
    if (!currentItem?.id) return;

    setIsLoadingModalDelete(true);
    setBusy(currentItem.id, true);

    try {
      await formAPI.delete(currentItem.id);
      HallAppToast.success("Formulário excluído!");
      setShowModalDelete(false);
      setCurrentItem(null);
      await getForms();
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalDelete(false);
      setBusy(currentItem?.id, false);
    }
  }

  return (
    <>
      <div className="container">
        <MenuTitle title="Formulários" showArrowInfo={false} />

        <div className="ft-headerMeta">
          <div className="ft-headerMeta__item">
            <span className="ft-headerMeta__label">Total</span>
            <span className="ft-headerMeta__value">{items.length}</span>
          </div>

          <div className="ft-headerMeta__item is-active">
            <span className="ft-headerMeta__label">Disponíveis</span>
            <span className="ft-headerMeta__value">{items.filter((f) => f.active).length}</span>
          </div>
        </div>

        <div className="ft-content">
          {/* Busca */}
          <Row className="g-2">
            <Col xs={12} md={12}>
              <AppInput
                label="Pesquisar"
                name="q"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                errors={[]}
                touched={[]}
                placeholder="Nome, descrição ou tipo…"
              />
            </Col>
            <hr />
          </Row>

          {/* Lista */}
          {loadingList ? (
            <div className="ft-list">
              <FormTemplateCardSkeleton count={6} />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              title="Nenhum formulário encontrado"
              description="Tente buscar por outro termo ou crie um novo formulário."
              icon={<span><i><span /></i></span>}
              // seu EmptyState já usa icon, mantive o calendário como antes:
              // (se quiser, volta pro FontAwesomeIcon aqui)
              actionLabel="Criar formulário"
              onAction={onCreate}
            />
          ) : (
            <Row className="g-3" style={{ margin: 0, width: "100%" }}>
              {filtered.map((it) => {
                const busy = !!busyById[it.id];

                return (
                  <Col xs={12} sm={6} md={4} lg={4} key={it.id} className="mb-3">
                    <div style={{ width: "100%", opacity: busy ? 0.85 : 1 }}>
                      <CustomCard>
                        {/* Header */}
                        <div className="d-flex align-items-start justify-content-between gap-2">
                          <div style={{ minWidth: 0 }}>
                            <div className="fw-semibold" style={{ fontSize: 16, lineHeight: "20px" }}>
                              {it.name}
                            </div>
                            <div
                              className="text-muted"
                              style={{
                                fontSize: 12,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={it.description || ""}
                            >
                              {it.description || "—"}
                            </div>
                          </div>

                          <Badge bg={it.active ? "success" : "secondary"} pill style={{ whiteSpace: "nowrap" }}>
                            {it.active ? "Ativo" : "Inativo"}
                          </Badge>
                        </div>

                        <hr className="my-3" />

                        {/* Infos principais */}
                        <div className="d-flex flex-column gap-2">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="text-muted" style={{ fontSize: 13 }}>
                              Tipo
                            </div>
                            <Badge bg="info" pill style={{ fontSize: 12 }}>
                              {typeLabel(it.templateType)}
                            </Badge>
                          </div>

                          <div className="d-flex align-items-center justify-content-between">
                            <div className="text-muted" style={{ fontSize: 13 }}>
                              Campos
                            </div>
                            <div className="fw-semibold" style={{ fontSize: 13 }}>
                              {it.fieldsCount ?? 0}
                            </div>
                          </div>

                          <div className="d-flex align-items-center justify-content-between">
                            <div className="text-muted" style={{ fontSize: 13 }}>
                              Criado em
                            </div>
                            <div className="fw-semibold" style={{ fontSize: 13 }}>
                              {formatDate(it.createDt)}
                            </div>
                          </div>
                        </div>

                        <hr className="my-3" />

                        {/* Ações com ActionPillButton */}
                        <div className="d-flex flex-wrap gap-2">
                          <ActionPillButton
                            label="Editar"
                            icon={faPen}
                            onClick={() => onEdit(it.id)}
                            disabled={busy}
                            title="Editar formulário"
                            size="sm"
                          />

                          <ActionPillButton
                            label={busy ? "Salvando..." : it.active ? "Inativar" : "Ativar"}
                            icon={faPowerOff}
                            onClick={() => onToggleActive(it)}
                            disabled={busy}
                            title={it.active ? "Inativar formulário" : "Ativar formulário"}
                            size="sm"
                            variant={it.active ? "outline" : "solid"}
                            color={it.active ? "#111827" : "#16a34a"}
                            backgroundColor={it.active ? "#ffffff" : "#16a34a"}
                            borderColor={it.active ? "#e8e8f0" : "#16a34a"}
                          />

                          <ActionPillButton
                            label="Visualizar"
                            icon={faEye}
                            onClick={() => onView(it.id)}
                            disabled={busy}
                            title="Abrir formulário em nova aba"
                            size="sm"
                          />

                          <ActionPillButton
                            label="Excluir"
                            icon={faTrash}
                            onClick={() => onAskDelete(it)}
                            disabled={busy}
                            title="Excluir formulário"
                            size="sm"
                            variant="outline"
                            color="#b91c1c"
                            borderColor="#fecaca"
                            backgroundColor="#ffffff"
                          />
                        </div>
                      </CustomCard>
                    </div>
                  </Col>
                );
              })}

              <div style={{ height: "calc(50px + 72px)" }} />
            </Row>
          )}
        </div>

        <FloatingButton icon={faAdd} onClick={onCreate} />

        {showModalDelete && (
          <ConfirmModal
            show={showModalDelete}
            title="Excluir"
            isLoading={isLoadingModalDelete}
            confirm={executeDeletion}
            closeModal={() => {
              setShowModalDelete(false);
              setCurrentItem(null);
            }}
          >
            Deseja remover o formulário <b>{currentItem?.name}</b>?
          </ConfirmModal>
        )}

        <SpaceMenuBottom />
      </div>
    </>
  );
}